import {
  Alert,
  Button,
  Input,
  Modal,
  Space,
  Typography,
  notification,
} from "antd";
import { useEffect, useState } from "react";
import { TranslationOutlined } from "@ant-design/icons";

import { Post, Put } from "../../fetch";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * Hindi localization for one curated offer — name/description/goal steps
 * only (never icon/coins/trk_url, nothing else the app doesn't render).
 * "Translate with Gemini" calls the backend, which re-derives these fields
 * from the offer's CURRENT English text and saves the result; the admin can
 * then hand-edit before hitting Save (Save always stores verbatim, same as
 * a manual edit — no AI call).
 */
const LocalizeOffer = ({ visible, onClose, apiUrl, token, offer, onSaved }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [goals, setGoals] = useState([]);
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const goalCount = (offer?.goals || []).length;

  useEffect(() => {
    if (!visible || !offer) return;
    const hi = offer.translations?.hi || {};
    setName(hi.name || "");
    setDescription(hi.description || "");
    setGoals(
      (offer.goals || []).map((g, i) => ({
        title: hi.goals?.[i]?.title || "",
        instructions: hi.goals?.[i]?.instructions || "",
      }))
    );
    setErrorMsg(null);
  }, [visible, offer]);

  const setGoal = (i, patch) =>
    setGoals((prev) => prev.map((g, idx) => (idx === i ? { ...g, ...patch } : g)));

  const handleTranslate = () => {
    setTranslating(true);
    setErrorMsg(null);
    Post(
      `${apiUrl}/v4/tasks-config/offers/${offer.id}/translate?lang=hi`,
      token,
      {},
      (result) => {
        setTranslating(false);
        if (result && result.success) {
          const hi = result.offer?.translations?.hi || {};
          setName(hi.name || "");
          setDescription(hi.description || "");
          setGoals(
            (offer.goals || []).map((g, i) => ({
              title: hi.goals?.[i]?.title || "",
              instructions: hi.goals?.[i]?.instructions || "",
            }))
          );
          notification.success({ message: "Translated", description: "Review and Save below." });
        } else {
          setErrorMsg((result && result.message) || "Translation failed. Check GEMINI_API_KEY on setu.");
        }
      },
      () => {
        setTranslating(false);
        setErrorMsg("Translation failed. Check console / GEMINI_API_KEY on setu.");
      }
    );
  };

  const handleSave = () => {
    setSaving(true);
    setErrorMsg(null);
    const payload = {
      translations: {
        ...(offer.translations || {}),
        hi: {
          name: name.trim(),
          description: description.trim(),
          // Category label carries through from the last Gemini translate —
          // not directly editable here (category is a fixed 3-value enum).
          ...(offer.translations?.hi?.category ? { category: offer.translations.hi.category } : {}),
          goals: goals.map((g) => ({ title: g.title.trim(), instructions: g.instructions.trim() })),
        },
      },
    };
    Put(
      `${apiUrl}/v4/tasks-config/offers/${offer.id}`,
      token,
      payload,
      (result) => {
        setSaving(false);
        if (result && result.success) {
          notification.success({ message: "Saved", description: "Hindi localization saved." });
          onSaved();
          onClose();
        } else {
          setErrorMsg((result && result.message) || "Save failed.");
        }
      },
      () => {
        setSaving(false);
        setErrorMsg("Save failed. Check console.");
      }
    );
  };

  return (
    <Modal
      title={`Localize (Hindi) — ${offer?.name || ""}`}
      visible={visible}
      onCancel={onClose}
      width={720}
      maskClosable={false}
      footer={[
        <Button key="translate" icon={<TranslationOutlined />} loading={translating} onClick={handleTranslate}>
          Translate with Gemini
        </Button>,
        <Button key="cancel" onClick={onClose} disabled={saving}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          Save
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        {errorMsg ? <Alert type="error" showIcon message={errorMsg} /> : null}

        <div>
          <Text strong>Name (Hindi)</Text>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="हिंदी नाम"
            style={{ marginTop: 4 }}
          />
        </div>

        <div>
          <Text strong>Description (Hindi)</Text>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="हिंदी विवरण"
            autoSize={{ minRows: 2, maxRows: 4 }}
            style={{ marginTop: 4 }}
          />
        </div>

        {goalCount > 0 && (
          <div>
            <Text strong>Goals / Steps (Hindi)</Text>
            <div style={{ maxHeight: 360, overflowY: "auto", marginTop: 4 }}>
              {(offer.goals || []).map((g, i) => (
                <div
                  key={i}
                  style={{
                    border: "1px solid #f0f0f0",
                    borderRadius: 8,
                    padding: "8px 12px",
                    marginBottom: 8,
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Step {i + 1} — EN: {g.title}
                  </Text>
                  <Input
                    value={goals[i]?.title || ""}
                    onChange={(e) => setGoal(i, { title: e.target.value })}
                    placeholder="हिंदी शीर्षक"
                    style={{ marginTop: 4, marginBottom: 6 }}
                  />
                  <TextArea
                    value={goals[i]?.instructions || ""}
                    onChange={(e) => setGoal(i, { instructions: e.target.value })}
                    placeholder="हिंदी निर्देश"
                    autoSize={{ minRows: 2, maxRows: 6 }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default LocalizeOffer;
